/**
 * AdminCareLibraryPanel — manage the caregiver + clinician video library.
 *
 * Endpoints used:
 *   GET    /api/care-library/items                (public read; merges seed + admin rows)
 *   POST   /api/admin/care-library/items          (upsert by id)
 *   DELETE /api/admin/care-library/items/{id}     (custom rows only; seed protected)
 *
 * Behavior:
 *   - Seed rows (the 17 hard-coded entries in backend/routes/care_library.py)
 *     are read-only here — we surface them with a "seed" pill so admins
 *     know they can't be edited in the UI. To override a seed row, the
 *     admin adds a custom row with the SAME id and it wins on read.
 *   - Custom (DB) rows are fully editable and deletable.
 *   - The form enforces §5 of /app/memory/VIDEO_CURATION_PROMPT.md —
 *     license/attribution required, lane must be A or B, image_caveat
 *     flag explicit, accreditation disclosure honest.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Plus, RefreshCw, Trash2, Pencil, Lock, Video, X } from 'lucide-react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { useModalClose, CLOSE_BTN_STYLE } from '../../hooks/useModalClose';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOPIC_CAREGIVER = [
  'orientation', 'safe-physical-care', 'medication-management', 'dementia',
  'caregiver-self-care', 'nutrition', 'home-safety', 'end-of-life',
  'care-navigation', 'hospice-navigation',
];
const TOPIC_CLINICIAN = [
  'hospice-eligibility', 'symptom-management', 'goals-of-care-communication',
  'documentation-compliance', 'payment-basics', 'infection-control',
  'wound-care', 'medication-safety', 'cultural-humility',
  'interdisciplinary-teamwork',
];

const EMPTY_FORM = {
  id: '', title: '', audience: 'caregiver', topic: 'orientation',
  source_org: '', url: '', lane: 'A', license: '', license_url: '',
  attribution: '', image_caveat: false, clinical_review_date: '',
  accreditation: '', captions: true, languages: 'en', runtime_seconds: '',
  thumbnail_url: '', notes: '', navigator_keywords: '',
};

export function AdminCareLibraryPanel({ getAuthHeader }) {
  const [items, setItems] = useState([]);
  const [customIds, setCustomIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingExistingId, setEditingExistingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [audienceFilter, setAudienceFilter] = useState('all');
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/care-library/items`);
      const list = r.data?.items || [];
      setItems(list);
      // We can't tell seed-vs-custom from the public API alone — admin rows
      // are merged in. Track custom ids by re-fetching from admin route
      // (skipped for now; relying on `updated_by` field presence).
      const cust = new Set(list.filter((i) => i.updated_by).map((i) => i.id));
      setCustomIds(cust);
    } catch (e) {
      toast.error('Could not load the video catalog');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    let xs = items;
    if (audienceFilter !== 'all') xs = xs.filter((i) => i.audience === audienceFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      xs = xs.filter((i) => i.title.toLowerCase().includes(q) || i.id.toLowerCase().includes(q) || (i.source_org || '').toLowerCase().includes(q));
    }
    return xs;
  }, [items, audienceFilter, search]);

  const openAdd = () => {
    setForm(EMPTY_FORM);
    setEditingExistingId(null);
    setShowForm(true);
  };

  const openEdit = (item) => {
    setForm({
      ...EMPTY_FORM,
      ...item,
      languages: (item.languages || ['en']).join(', '),
      navigator_keywords: (item.navigator_keywords || []).join(', '),
      runtime_seconds: item.runtime_seconds || '',
      license_url: item.license_url || '',
      accreditation: item.accreditation || '',
      thumbnail_url: item.thumbnail_url || '',
      notes: item.notes || '',
      clinical_review_date: item.clinical_review_date || '',
    });
    setEditingExistingId(item.id);
    setShowForm(true);
  };

  const submit = async () => {
    // §5 validation — refuse to ship items missing the verification fields
    const missing = [];
    if (!form.id.trim()) missing.push('id');
    if (!form.title.trim()) missing.push('title');
    if (!form.source_org.trim()) missing.push('source_org');
    if (!form.url.trim()) missing.push('url');
    if (!form.license.trim()) missing.push('license');
    if (!form.attribution.trim()) missing.push('attribution');
    if (!['A', 'B'].includes(form.lane)) missing.push('lane');
    if (missing.length) {
      toast.error(`Missing required §5 fields: ${missing.join(', ')}`);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        languages: form.languages.split(',').map((s) => s.trim()).filter(Boolean),
        navigator_keywords: form.navigator_keywords ? form.navigator_keywords.split(',').map((s) => s.trim()).filter(Boolean) : [],
        runtime_seconds: form.runtime_seconds ? parseInt(form.runtime_seconds, 10) : null,
        license_url: form.license_url || null,
        accreditation: form.accreditation || null,
        thumbnail_url: form.thumbnail_url || null,
        notes: form.notes || null,
        clinical_review_date: form.clinical_review_date || null,
      };
      await axios.post(`${API}/admin/care-library/items`, payload, getAuthHeader());
      toast.success(editingExistingId ? 'Video updated' : 'Video added');
      setShowForm(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save the video');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item) => {
    if (!customIds.has(item.id)) {
      toast.error('Seed rows are protected — to override, add a custom row with the same id.');
      return;
    }
    if (!window.confirm(`Delete "${item.title}"?`)) return;
    try {
      await axios.delete(`${API}/admin/care-library/items/${encodeURIComponent(item.id)}`, getAuthHeader());
      toast.success('Removed');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not delete');
    }
  };

  return (
    <div data-testid="admin-care-library-panel">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Button onClick={openAdd} className="cn-btn cn-btn-primary" data-testid="care-library-add-btn">
          <Plus className="w-4 h-4" /> Add video
        </Button>
        <Button onClick={load} variant="outline" disabled={loading} data-testid="care-library-refresh-btn">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Refresh
        </Button>
        <div className="ml-auto flex items-center gap-2">
          <select value={audienceFilter} onChange={(e) => setAudienceFilter(e.target.value)} className="border border-cn-border rounded-md px-2 py-1.5 text-sm bg-white" data-testid="care-library-audience-filter">
            <option value="all">All audiences</option>
            <option value="caregiver">Caregiver</option>
            <option value="clinician">Clinician</option>
          </select>
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…" className="w-48 text-sm" data-testid="care-library-search" />
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center py-12 text-cn-text-muted"><Loader2 className="w-5 h-5 inline-block animate-spin mr-2" /> Loading catalog…</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-wider text-cn-text-muted border-b border-cn-border">
              <tr>
                <th className="text-left py-2 px-2">Origin</th>
                <th className="text-left py-2 px-2">Title</th>
                <th className="text-left py-2 px-2">Audience</th>
                <th className="text-left py-2 px-2">Topic</th>
                <th className="text-left py-2 px-2">Source</th>
                <th className="text-left py-2 px-2">Lane</th>
                <th className="text-left py-2 px-2">Image caveat</th>
                <th className="text-left py-2 px-2">Validation</th>
                <th className="text-right py-2 px-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((item) => {
                const isCustom = customIds.has(item.id);
                return (
                  <tr key={item.id} className="border-b border-cn-border/40 hover:bg-cn-bg/30" data-testid={`care-library-row-${item.id}`}>
                    <td className="py-2 px-2">
                      {isCustom ? (
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-cn-primary/10 text-cn-primary">custom</span>
                      ) : (
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-cn-bone-100 text-cn-text-muted inline-flex items-center gap-1"><Lock className="w-2.5 h-2.5" />seed</span>
                      )}
                    </td>
                    <td className="py-2 px-2 max-w-[280px]"><div className="font-semibold truncate">{item.title}</div><div className="text-[10px] text-cn-text-muted truncate">{item.id}</div></td>
                    <td className="py-2 px-2">{item.audience}</td>
                    <td className="py-2 px-2">{item.topic}</td>
                    <td className="py-2 px-2 max-w-[180px] truncate">{item.source_org}</td>
                    <td className="py-2 px-2"><span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${item.lane === 'A' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{item.lane}</span></td>
                    <td className="py-2 px-2">{item.image_caveat ? <span className="text-amber-700">⚠ yes</span> : <span className="text-cn-text-muted">no</span>}</td>
                    <td className="py-2 px-2 text-[10px] text-cn-text-muted">{item.clinical_review_date || '—'}</td>
                    <td className="py-2 px-2 text-right">
                      <Button onClick={() => openEdit(item)} variant="ghost" size="sm" data-testid={`care-library-edit-${item.id}`} title={isCustom ? 'Edit' : 'View / override (creates a custom row)'}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button onClick={() => remove(item)} variant="ghost" size="sm" disabled={!isCustom} className="text-rose-600 disabled:opacity-30" data-testid={`care-library-delete-${item.id}`} title={isCustom ? 'Delete' : 'Seed rows protected'}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <CareLibraryForm
          form={form}
          setForm={setForm}
          editing={!!editingExistingId}
          saving={saving}
          topics={form.audience === 'caregiver' ? TOPIC_CAREGIVER : TOPIC_CLINICIAN}
          onClose={() => setShowForm(false)}
          onSubmit={submit}
        />
      )}
    </div>
  );
}

function CareLibraryForm({ form, setForm, editing, saving, topics, onClose, onSubmit }) {
  const { backdropProps, cardProps } = useModalClose({
    open: true,
    onClose: saving ? () => {} : onClose,
    dismissOnEscape: !saving,
    dismissOnBackdrop: !saving,
  });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setChk = (k) => (e) => setForm({ ...form, [k]: e.target.checked });

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" {...backdropProps} data-testid="care-library-form-modal">
      <div className="relative w-full max-w-3xl rounded-3xl bg-white dark:bg-cn-ink-800 border border-cn-border shadow-2xl overflow-hidden flex flex-col max-h-[90vh]" {...cardProps}>
        <div className="p-5 text-white flex items-start justify-between" style={{ background: 'linear-gradient(135deg, #0F172A, #334155)' }}>
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-white/15 flex items-center justify-center"><Video className="w-5 h-5" /></div>
            <div>
              <p className="text-[11px] uppercase tracking-[0.14em] font-bold text-white/70">Care Library</p>
              <h3 className="font-display font-bold text-xl">{editing ? 'Edit / override video' : 'Add a video'}</h3>
              <p className="text-[11px] text-white/70 mt-0.5">All fields required per §5 of the curation prompt. Custom row will override the seed if the id matches.</p>
            </div>
          </div>
          <button type="button" onClick={onClose} disabled={saving} aria-label="Close" className="flex items-center justify-center rounded-full hover:bg-white/15 active:bg-white/25 transition-colors touch-manipulation disabled:opacity-50" style={CLOSE_BTN_STYLE} data-testid="care-library-form-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 overflow-y-auto space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Stable id (slug)" required>
              <Input value={form.id} onChange={set('id')} placeholder="e.g. nia.dementia-tips" data-testid="form-id" />
            </Field>
            <Field label="Audience" required>
              <select value={form.audience} onChange={set('audience')} className="w-full border border-cn-border rounded-md px-2 py-2 text-sm bg-white" data-testid="form-audience">
                <option value="caregiver">Caregiver</option>
                <option value="clinician">Clinician</option>
              </select>
            </Field>
            <Field label="Title" required wide>
              <Input value={form.title} onChange={set('title')} data-testid="form-title" />
            </Field>
            <Field label="Topic" required>
              <select value={form.topic} onChange={set('topic')} className="w-full border border-cn-border rounded-md px-2 py-2 text-sm bg-white" data-testid="form-topic">
                {topics.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Lane (A=re-host · B=embed)" required>
              <select value={form.lane} onChange={set('lane')} className="w-full border border-cn-border rounded-md px-2 py-2 text-sm bg-white" data-testid="form-lane">
                <option value="A">A — re-host (federal PD / CC0 / CC BY)</option>
                <option value="B">B — embed/link only</option>
              </select>
            </Field>
            <Field label="Source organization" required>
              <Input value={form.source_org} onChange={set('source_org')} data-testid="form-source-org" />
            </Field>
            <Field label="URL" required>
              <Input value={form.url} onChange={set('url')} data-testid="form-url" />
            </Field>
            <Field label="License (exact basis)" required wide>
              <Input value={form.license} onChange={set('license')} placeholder="e.g. U.S. federal public domain (17 U.S.C. §105)" data-testid="form-license" />
            </Field>
            <Field label="License URL (optional)" wide>
              <Input value={form.license_url} onChange={set('license_url')} placeholder="https://…" data-testid="form-license-url" />
            </Field>
            <Field label="Attribution string (verbatim)" required wide>
              <Textarea value={form.attribution} onChange={set('attribution')} rows={2} data-testid="form-attribution" />
            </Field>
            <Field label="Image caveat?" inline>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!form.image_caveat} onChange={setChk('image_caveat')} data-testid="form-image-caveat" />
                Source warns embedded imagery may be third-party copyrighted
              </label>
            </Field>
            <Field label="Captions present?" inline>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!form.captions} onChange={setChk('captions')} data-testid="form-captions" />
                ADA / Section 508 ready
              </label>
            </Field>
            <Field label="Clinical review date">
              <Input value={form.clinical_review_date} onChange={set('clinical_review_date')} placeholder="YYYY-MM" data-testid="form-review-date" />
            </Field>
            <Field label="Runtime (seconds)">
              <Input value={form.runtime_seconds} onChange={set('runtime_seconds')} placeholder="e.g. 240" data-testid="form-runtime" />
            </Field>
            <Field label="Languages (comma-sep)">
              <Input value={form.languages} onChange={set('languages')} placeholder="en, es" data-testid="form-languages" />
            </Field>
            <Field label="Accreditation (clinician items)" wide>
              <Input value={form.accreditation} onChange={set('accreditation')} placeholder='e.g. "credit-bearing (at source) — ACCME AMA PRA Category 1"' data-testid="form-accreditation" />
            </Field>
            <Field label="Thumbnail URL" wide>
              <Input value={form.thumbnail_url} onChange={set('thumbnail_url')} placeholder="https://…" data-testid="form-thumbnail" />
            </Field>
            <Field label="Navigator keywords (comma-sep)" wide>
              <Input value={form.navigator_keywords} onChange={set('navigator_keywords')} placeholder='e.g. "dad fell, fall risk"' data-testid="form-keywords" />
            </Field>
            <Field label="Internal notes" wide>
              <Textarea value={form.notes} onChange={set('notes')} rows={2} data-testid="form-notes" />
            </Field>
          </div>
        </div>
        <div className="p-4 border-t border-cn-border flex items-center justify-end gap-2">
          <Button onClick={onClose} variant="outline" disabled={saving} data-testid="form-cancel">Cancel</Button>
          <Button onClick={onSubmit} disabled={saving} className="cn-btn cn-btn-primary" data-testid="form-submit">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editing ? 'Save changes' : 'Add to catalog')}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, required, wide, inline, children }) {
  return (
    <div className={`${wide ? 'sm:col-span-2' : ''} ${inline ? 'sm:col-span-1' : ''}`}>
      <Label className="text-[10px] uppercase tracking-wider font-bold text-cn-text-muted">
        {label}{required ? <span className="text-rose-600"> *</span> : null}
      </Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

export default AdminCareLibraryPanel;
